/**
 * NoteCatchUpGate — full-screen "catch up on your notes" overlay (CLAUDE.md §31.9).
 *
 * When the ACTIVE ROLE has any unacknowledged notes (file notes routed to it,
 * prep notes routed to it), this overlay gates the whole app: it lists those
 * notes with inline "Mark as read" checkboxes and cannot be dismissed until
 * every listed note is acknowledged. Exception: the Adviser role gets a
 * "Skip for now" escape (and Escape key = skip); assistant roles have no skip.
 *
 * Trigger points (the pending check re-derives from the DB each time — there
 * is no separate state store):
 *   a) app load / login, once the session + active role are resolved
 *   b) every active-role switch (the `role` context value changes)
 *   c) window focus / visibility regained after ≥ 15 minutes hidden
 * A new note landing mid-session does NOT interrupt — between trigger points
 * the flashing card (owner-only illumination, §31.4) is the signal.
 *
 * Sealed-card consistency (Inbox draw gate, §40): notes on a `submitted`
 * (sealed) file are excluded from the Administration Assistant's gate list,
 * and notes on a sealed preparation request from the New Business
 * Assistant's, mirroring the §31.4 rule that suppresses their note badge and
 * illumination until the card is drawn. They gate normally once the record
 * leaves Inbox. One helper, isSealedInInbox, holds the rule.
 *
 * "Open file →" / "Open request →" links navigate straight to the item's
 * detail view and dismiss the overlay WITHOUT acknowledging the note — the
 * note stays unread, so the gate simply reappears on the next dashboard
 * load. Role gating is unchanged: the links use the same `/file/:id` and
 * `/prep/:id` routes as the dashboard cards, whose detail views enforce
 * the §30 role rules.
 *
 * There is no "Continue to dashboard" button: marking the LAST unread note
 * as read auto-closes the overlay (same `entries`/`acked` source of truth
 * as the "X of Y still to read" counter). "Skip for now" (Adviser only)
 * remains the only explicit dismissal while unread notes remain.
 *
 * Acknowledging here uses the same RPCs as the detail views; opening items,
 * logging in, and closing the overlay never auto-acknowledge anything.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import {
  getPendingNotesForRole as getPendingFileNotes,
  acknowledgeNote as acknowledgeFileNote,
} from '../../api/fileNotesApi';
import {
  getPendingNotesForRole as getPendingPrepNotes,
  acknowledgeNote as acknowledgePrepNote,
} from '../../api/prepNotesApi';
import { isSealedInInbox } from '../../utils/inboxDrawGate';

// Re-check on refocus only after this long hidden/idle (spec: ≥ 15 minutes).
const REFOCUS_IDLE_MS = 15 * 60 * 1000;

// First-name lookup for the greeting — mirrors the EMAIL_TO_FIRST_NAME
// convention used in the views (there is no profiles table).
const EMAIL_TO_FIRST_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
};
function firstNameOf(email) {
  return EMAIL_TO_FIRST_NAME[(email || '').toLowerCase()] || (email ? email.split('@')[0] : 'there');
}

function formatNoteStamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} · ${hh}:${mi}`;
}

export default function NoteCatchUpGate() {
  const { user, role } = useAuth();
  const navigate = useNavigate();

  // entries: [{ kind: 'file' | 'prep', note }] — note carries clientName.
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [acked, setAcked] = useState(() => new Set());
  const [ackingId, setAckingId] = useState(null);
  const [error, setError] = useState(null);

  const cardRef = useRef(null);
  const hiddenAtRef = useRef(null);
  const openRef = useRef(false);
  const ackedCountRef = useRef(0);
  openRef.current = open;
  ackedCountRef.current = acked.size;

  const isAdviser = role === 'adviser';

  // The single pending check. Re-derives entirely from the DB; failures
  // degrade to "no gate" (never block the app on a fetch error).
  const runCheck = useCallback(async () => {
    if (!role) return;
    try {
      const [fileNotes, prepNotes] = await Promise.all([
        getPendingFileNotes(role).catch(() => []),
        getPendingPrepNotes(role).catch(() => []),
      ]);
      // Sealed-card rule (§31.4 / §40): the Administration Assistant does
      // not see notes on files still in Inbox until the file is drawn, and
      // the New Business Assistant does not see notes on preparation
      // requests still in Inbox until she starts them. Same helper as the
      // detail views and the board, so the three can never disagree.
      const visibleFileNotes = fileNotes.filter(
        (n) => !isSealedInInbox('file', n.fileStatus, role)
      );
      const visiblePrepNotes = prepNotes.filter(
        (n) => !isSealedInInbox('prep', n.prepStatus, role)
      );
      const combined = [
        ...visibleFileNotes.map((note) => ({ kind: 'file', note })),
        ...visiblePrepNotes.map((note) => ({ kind: 'prep', note })),
      ].sort((a, b) => new Date(a.note.createdAt) - new Date(b.note.createdAt));

      if (combined.length > 0) {
        setEntries(combined);
        setAcked(new Set());
        setError(null);
        setOpen(true);
      } else {
        setOpen(false);
        setEntries([]);
      }
    } catch (e) {
      console.warn('[NoteCatchUpGate] pending check failed:', e);
    }
  }, [role]);

  // Trigger (a) + (b): app load once the role resolves, and every role switch.
  useEffect(() => {
    runCheck();
  }, [runCheck]);

  // Trigger (c): window refocus / visibility regained after ≥ 15 min hidden.
  // Skipped while the overlay is already open (re-running would reset the
  // user's ticking progress for no reason).
  useEffect(() => {
    const maybeRecheck = () => {
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (!hiddenAt || openRef.current) return;
      if (Date.now() - hiddenAt >= REFOCUS_IDLE_MS) runCheck();
    };
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAtRef.current = hiddenAtRef.current || Date.now();
      } else {
        maybeRecheck();
      }
    };
    const onFocus = () => maybeRecheck();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [runCheck]);

  // Notify dashboards (they refetch counts) whenever the overlay closes after
  // at least one acknowledgement — cards stop flashing without a reload.
  const close = useCallback(() => {
    if (ackedCountRef.current > 0) {
      window.dispatchEvent(new CustomEvent('inbox:notes-acknowledged'));
    }
    setOpen(false);
  }, []);

  const allDone = entries.length > 0 && entries.every((e) => acked.has(e.note.id));
  const remaining = entries.length - acked.size;

  const acknowledge = async (entry) => {
    if (ackingId || acked.has(entry.note.id)) return;
    setAckingId(entry.note.id);
    setError(null);
    // Optimistic tick; revert on RPC failure.
    setAcked((prev) => new Set(prev).add(entry.note.id));
    try {
      if (entry.kind === 'file') await acknowledgeFileNote(entry.note.id);
      else await acknowledgePrepNote(entry.note.id);
    } catch (e) {
      setAcked((prev) => {
        const next = new Set(prev);
        next.delete(entry.note.id);
        return next;
      });
      setError(e.message || 'Could not mark the note as read. Please try again.');
    } finally {
      setAckingId(null);
    }
  };

  const openItem = (entry) => {
    // Navigating does NOT acknowledge the note — it stays unread, so the
    // gate reappears on the next dashboard load. That is intended.
    close();
    navigate(entry.kind === 'file' ? `/file/${entry.note.fileId}` : `/prep/${entry.note.prepRequestId}`);
  };

  // Auto-close: when the last unread note is marked as read, the overlay
  // dismisses itself and the user lands on the dashboard. Waits for the
  // in-flight RPC to settle (ackingId cleared) so a failed final ack —
  // whose optimistic tick is reverted — never closes the gate.
  useEffect(() => {
    if (open && allDone && !ackingId) close();
  }, [open, allDone, ackingId, close]);

  // Focus + keyboard gating while open: focus moves into the modal; Tab is
  // trapped inside it; Escape only acts for the Adviser (= Skip for now).
  useEffect(() => {
    if (!open) return undefined;
    const card = cardRef.current;
    if (card) card.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // app behind is inert

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (isAdviser) close();
        return;
      }
      if (e.key === 'Tab' && card) {
        const focusables = card.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (!card.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isAdviser, close]);

  if (!open || entries.length === 0) return null;

  return (
    <div style={styles.backdrop} role="presentation">
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="New notes awaiting your acknowledgement"
        style={styles.card}
      >
        <div style={styles.header}>
          <h2 style={styles.title}>New notes for you, {firstNameOf(user?.email)}</h2>
          <p style={styles.subtitle}>
            {isAdviser
              ? 'These notes are waiting on you. Mark each as read to continue — or skip for now.'
              : 'These notes are waiting on you. The dashboard unlocks once every note is marked as read.'}
          </p>
        </div>

        <div style={styles.list}>
          {entries.map((entry) => {
            const { note, kind } = entry;
            const isRead = acked.has(note.id);
            return (
              <div
                key={note.id}
                style={{ ...styles.entry, ...(isRead ? styles.entryRead : styles.entryPending) }}
              >
                <div style={styles.entryTop}>
                  <span style={{ ...styles.kindChip, ...(kind === 'prep' ? styles.kindChipPrep : {}) }}>
                    {kind === 'file' ? 'FILE FOLDER' : 'PREP REQUEST'}
                  </span>
                  <span style={styles.clientName}>{note.clientName || 'Unknown client'}</span>
                  <span style={styles.stamp}>
                    {note.authorDisplayName} · {formatNoteStamp(note.createdAt)}
                  </span>
                </div>
                <div style={styles.body}>{note.body}</div>
                <div style={styles.entryControls}>
                  {isRead ? (
                    <span style={styles.readBadge}>✓ Read</span>
                  ) : (
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={false}
                        disabled={ackingId === note.id}
                        onChange={() => acknowledge(entry)}
                        style={styles.checkbox}
                      />
                      <span>{ackingId === note.id ? 'Marking…' : 'Mark as read'}</span>
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => openItem(entry)}
                    style={styles.openLink}
                  >
                    {kind === 'file' ? 'Open file →' : 'Open request →'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.footer}>
          <span style={styles.progress}>
            {remaining === 0
              ? 'All caught up!'
              : `${remaining} of ${entries.length} still to read`}
          </span>
          {isAdviser && (
            <div style={styles.footerButtons}>
              <button type="button" className="btn btn-secondary" onClick={close}>
                Skip for now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// §31 amber family for pending, read-green for done. Amber stays distinct
// from red (URGENT/overdue) and teal/blue (status colours).
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 3000,
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  card: {
    background: 'var(--cp-surface)',
    borderRadius: '16px',
    boxShadow: '0 24px 70px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '640px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    outline: 'none',
  },
  header: {
    padding: '1.4rem 1.6rem 0.9rem',
    borderBottom: '1px solid var(--cp-surface-alt)',
  },
  title: {
    margin: 0,
    fontSize: '1.2rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
  },
  subtitle: {
    margin: '0.4rem 0 0',
    fontSize: '0.85rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.5,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '1rem 1.6rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  entry: {
    borderRadius: '10px',
    padding: '0.75rem 0.9rem',
    border: '1px solid',
  },
  entryPending: {
    background: 'var(--cp-ack-amber-soft)',            // amber soft
    borderColor: '#fde9c6',           // amber edge
    borderLeft: '4px solid var(--cp-ack-amber)',  // amber accent
  },
  entryRead: {
    background: '#e7f6ec',            // read-green soft
    borderColor: '#c9ead5',
    borderLeft: '4px solid var(--cp-read-green)',
  },
  entryTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  kindChip: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    padding: '2px 7px',
    borderRadius: '4px',
    background: 'rgba(15, 76, 117, 0.1)',
    color: 'var(--cp-primary)',
  },
  kindChipPrep: {
    background: 'rgba(244, 162, 97, 0.18)',
    color: '#9a5b1f',
  },
  clientName: {
    fontSize: '0.85rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
  },
  stamp: {
    marginLeft: 'auto',
    fontSize: '0.72rem',
    color: 'var(--cp-text-muted)',
  },
  body: {
    fontFamily: 'var(--font-handwritten)',
    fontSize: '19px',
    lineHeight: 1.35,
    color: '#2c2418',
    margin: '0.45rem 0 0.55rem',
    overflowWrap: 'anywhere',
  },
  entryControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  checkboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#7c5a12',                 // amber text
    cursor: 'pointer',
  },
  checkbox: {
    width: '15px',
    height: '15px',
    accentColor: 'var(--cp-ack-amber)',
    cursor: 'pointer',
  },
  readBadge: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: 'var(--cp-read-green)',
  },
  openLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-primary)',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  error: {
    margin: '0 1.6rem',
    padding: '0.5rem 0.8rem',
    borderRadius: '8px',
    background: 'rgba(231, 111, 81, 0.12)',
    border: '1px solid var(--cp-warning)',
    color: 'var(--cp-danger)',
    fontSize: '0.8rem',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    padding: '0.9rem 1.6rem 1.2rem',
    borderTop: '1px solid var(--cp-surface-alt)',
    flexWrap: 'wrap',
  },
  progress: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-text-muted)',
  },
  footerButtons: {
    display: 'flex',
    gap: '0.6rem',
  },
};
